import { makeAutoObservable } from "mobx";
import {
  DbotEvent,
  DecisionContext,
  DbotEventDispatcher,
  extractSectionWithMaxScoreFromArticles,
} from "@/dbot/common/commonDomain";

import { Language } from "@/i18n/languages";
import { LocalizedText } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import {
  ArticleSelectorModel,
  initArticleTextModels,
  LocalizedStringFieldModel,
} from "@/common/commonForms";

export type ChangeCompanyBoardInArticlesOfAssociation = {
  type: DecisionType.changeCompanyBoardInArticlesOfAssociation;
  paragraph?: number;
  texts?: LocalizedText[];
};

export class ChangeCompanyBoardInArticlesOfAssociationForm {
  readonly type = DecisionType.changeCompanyBoardInArticlesOfAssociation;

  dispatch: DbotEventDispatcher;
  data?: ChangeCompanyBoardInArticlesOfAssociation;

  paragraph: ArticleSelectorModel;
  texts: LocalizedStringFieldModel[];

  constructor(
    dispatch: DbotEventDispatcher,
    readonly context: DecisionContext<ChangeCompanyBoardInArticlesOfAssociation>,
    data?: ChangeCompanyBoardInArticlesOfAssociation,
  ) {
    makeAutoObservable(this, { type: false, data: false }, { autoBind: true });
    this.dispatch = dispatch;
    this.data = data;

    this.paragraph = new ArticleSelectorModel(context.inputState?.articles, data?.paragraph);
    this.texts = initArticleTextModels({ selectorModel: this.paragraph, value: data?.texts });
  }

  init() {
    if (!this.paragraph.selected) {
      this.dispatch({
        type: "dbot/explore-articles-of-association",
        articles: this.context.inputState?.articles,
      });
    }
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/explore-articles-of-association-success":
        if (!this.paragraph.selected) {
          this.setParagraphValue(
            extractSectionWithMaxScoreFromArticles(event.data.results, "BOARD"),
          );
        }
        break;
    }
  }

  get languages() {
    return this.context.inputState?.articles?.languages ?? [Language.NO];
  }

  validate(): boolean {
    for (const t of this.texts.values()) {
      t.model.validate();
    }
    return this.isValid;
  }

  setParagraphValue(newParagraph: number | undefined) {
    this.paragraph.setSelected(newParagraph);
    this.texts = initArticleTextModels({ selectorModel: this.paragraph });
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isValid(): boolean {
    return Array.from(this.texts.values()).every((t) => t.model.isValid) && this.paragraph.isValid;
  }
}
